/*
  Three shapes are used while drawing polygons:
  1. .gmPoly: A `google.maps.Polygon` (or Polyline) for the actual polygon being drawn by the user.
  2. ._ghostPolygonImpl: A triangular `google.maps.Polygon` showing the area that would be enclosed if the user created
    a new vertex. When visible, it should always have three vertices:
    1. The first vertex of gmPoly
    2. The final vertex of gmPoly
    3. The current position of the cursor
  3. ._leadingLine: A `Polyline` drawn between the last vertex of ._impl and the cursor.
    When visible, it should always have two vertices:
    1. The final vertex of gmPoly
    2. The current position of the cursor
*/

import {markRaw, reactive, watch} from 'vue';
import {MapService, gmAPI} from './MapService';
import {SubscriptionManager} from '@/lib/filters/subscriptions';

const formatter = new Intl.NumberFormat('en-US');

/**
 * Takes a list of latLng objects and computes the total distance between them. Returns a string formatted in feet or
 * miles depending on the distance.
 * @param  {...google.maps.LatLng} coords A list of latLng objects.
 * @return {string}
 */
const distanceBetween = (...coords) => {
  let accumulator = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    accumulator += (gmAPI.geometry.spherical.computeDistanceBetween(coords[i], coords[i + 1]));
  }
  const feet = Math.round(accumulator * 3.28084);
  return feet >= 5280 ? formatter.format((feet / 5280).toFixed(1)) + ' mi' : formatter.format(feet) + ' ft';
};

// Common options
const npOrange = '#DF8C3E';
const npBlue = '#0098DA';

const DBLCLICK_DISTANCE = 6;

// Default options for the ghost polygon
const ghostPolygonOptions = {
  strokeOpacity: 0.5,
  strokeWeight: 1.5,
  fillOpacity: 0.05,
  clickable: false, // Needed to allow click events to pass through polygon
  strokeColor: npOrange,
  fillColor: npOrange,
};

// Default options for the leading line
const leadingLineBaseOptions = {
  strokeOpacity: 0.75,
  strokeWeight: 2,
  clickable: false, // Needed to allow click events to pass through polygon
};

const baseOptions = {
  type: 'polygon',
  leadingLine: true,
  ghostPolygon: true,
  clickable: false, // Needed to allow click events to pass through polygon
  strokeOpacity: 1,
  strokeWeight: 4,
  fillOpacity: 0.15,
};

// Style object for the tooltip used with a measurement tool polyline
const tooltipStyle = [
  {style: 'position', value: 'absolute'},
  {style: 'zIndex', value: '10000'},
  {style: 'backgroundColor', value: npBlue},
  {style: 'color', value: 'white'},
  {style: 'padding', value: '2px 20px'},
  {style: 'borderRadius', value: '30px'},
  {style: 'display', value: 'none'},
  {style: 'pointerEvents', value: 'none'},
];

function BeyondMapsPoly(path = [], mainPolyOptions = {}) {
  // Set helper variable for discerning between polygon and polyline objects
  this.isPolyline = mainPolyOptions.type === 'polyline';

  this.strokeColor = mainPolyOptions.strokeColor ?? npOrange;
  this.fillColor = mainPolyOptions.fillColor ?? npOrange;
  // Merge in default options with passed in overrides
  mainPolyOptions = Object.assign(
    {strokeColor: this.strokeColor, fillColor: this.fillColor},
    baseOptions,
    mainPolyOptions,
  );

  const Constr = mainPolyOptions.type === 'polyline' ? gmAPI.Polyline : gmAPI.Polygon;
  // Initialize main poly object
  /** @type {google.maps.Polygon|google.maps.Polyline} */
  this.gmPoly = initPoly(new Constr(mainPolyOptions));

  // Keep track of initialized helper lines for updating and cleanup
  this._helperLines = [];

  // Optional initializers:
  // Initialize helper lines if provided
  if (mainPolyOptions.leadingLine) {
    // Set leading line colors based on whether polygon or polyline
    const leadingLineOptions = Object.assign({},
      leadingLineBaseOptions,
      {strokeColor: this.strokeColor, fillColor: this.fillColor},
    );
    this._leadingLine = initPoly(new gmAPI.Polyline(leadingLineOptions));
    this._helperLines.push(this._leadingLine);
  }

  if (mainPolyOptions.ghostPolygon) {
    this._ghostPolygon = initPoly(new Constr({
      ...ghostPolygonOptions,
      strokeColor: this.strokeColor,
      fillColor: this.fillColor,
    }));
    this._helperLines.push(this._ghostPolygon);
  }

  // Initialize path if provided
  if (path.length) {
    this.gmPoly.setPath(path);
  }

  // Private methods:
  this._stopWatch = watch(
    () => ({...this.gmPoly.latLngs}),
    () => {
      if (this._isListening) {
        this._helperLines.forEach(updateChildPoly(this.gmPoly));
      }
    },
    {deep: true},
  );

  // Additional properties:
  // Store function needed to register maps event listeners.
  this._addListener = gmAPI.event.addListener;
  this._listeners = [];
  this.totalDistance = '';
  this.area = this.isPolyline ? null : this.computeArea();
  this.calculateSF = mainPolyOptions.calculateSF ?? false;

  // Used for tracking state of service from component
  this.active = true;

  // Initialize tooltip if polyline
  if (this.isPolyline) {
    this.tooltip = document.createElement('div');
    this.tooltip.appendChild(document.createTextNode(''));
    tooltipStyle.forEach(({style, value}) => {
      if (style === 'backgroundColor') {
        // set the tooltip color to whatever the given color is
        this.tooltip.style[style] = mainPolyOptions.strokeColor ?? npOrange;
      } else {
        this.tooltip.style[style] = value;
      }
    });
    document.body.appendChild(this.tooltip);
  }

  this.subscriptions = new SubscriptionManager();
}

/**
 * Subscribes to an event for the poly
 * @param {string} event The event to subscribe to
 * @param {Function} callback The callback to be called when the event is triggered
 * @return {SubscriptionResult} An object with functions to alter the subscription.
 */
BeyondMapsPoly.prototype.subscribe = function(event, callback) {
  return this.subscriptions.addSubscription(event, callback);
};

/**
 * Returns formatted string square feet value
 * @return {string}
 */
BeyondMapsPoly.prototype.formattedArea = function() {
  return formatter.format(this.area) + ' sf';
};

/**
 * Returns formatted string acreage value
 * @return {string}
 */
BeyondMapsPoly.prototype.formattedAcreage = function() {
  return formatter.format((this.area / 43560).toFixed(2)) + ' acres';
};

/**
 * Helper function for initializing a `Polygon` or `Polyline`.
 * @param {google.maps.Polygon|google.maps.Polyline} poly A constructor for a `Polygon`
 * or `Polyline`.
 * @return {google.maps.Polygon|google.maps.Polyline} The initialized shape.
 */
const initPoly = (poly) => {
  // If the Polygon is not marked raw, it will re-appear even after being removed or set invisible.
  const result = markRaw(poly);
  // Need to make the polygon's paths reactive, since the polygon as a whole is raw.
  result.latLngs = reactive(result.latLngs);
  result.setMap(MapService.getMap());
  return result;
};

/**
 * Function used as a watcher for the underlying polygon's array of vertices.
 * Updates a child poly (either the leading line or ghost polygon) whenever the vertices change.
 * This is necessary because the user can click and drag the polygon to change its vertices without going through
 * any of our methods.
 * @param {google.maps.Polygon} poly The underlying Google Maps polygon
 * @return {function((google.maps.Polygon|google.maps.Polyline)): void}
 */
const updateChildPoly = (poly) => (comparee) => {
  const polyPath = poly.getPath();
  const compareePath = comparee.getPath();
  // Create an array of LatLng objects representing the child poly's current path.
  // This is the correct form to pass to setPath() later.
  const newPath = getVertices(comparee);

  if (polyPath.length && compareePath.length >= 2) {
    // Helper method that updates newPath if corresponding vertices between child and main polygon are not the same.
    // index is the index in newPath to update.
    // Takes advantage of the fact that, for both the leading line and ghost polygon, the (n-1)th vertex is always
    // the last vertex of the main polygon.
    const updateNewPathIfNeeded = (compareeVertex, polyVertex, index) => {
      if (!isSameVertex(compareeVertex, polyVertex)) {
        newPath[index].lat = polyVertex.lat();
        newPath[index].lng = polyVertex.lng();
      }
    };
    // This update will occur for both the ghost polygon and polyline.
    updateNewPathIfNeeded(vertexByEndDist(compareePath, 1), vertexByEndDist(polyPath, 0), compareePath.length - 2);
    // This additional update will occur for the ghost polygon, which has three vertices.
    if (compareePath.length >= 3) {
      updateNewPathIfNeeded(vertexByEndDist(compareePath, 2), polyPath.getAt(0), compareePath.length - 3);
    }
    comparee.setPath(newPath);
  }
};

// Helper functions for updateChildPoly
const vertexByEndDist = (path, dist) => path.getAt(path.length - 1 - dist);
const isSameVertex = (a, b) => a.lat() === b.lat() && a.lng() === b.lng();
const getVertices = (poly) => {
  const path = poly.getPath();
  if ('getArray' in path) {
    return path.getArray().map((vertex) => {
      return {
        lat: vertex.lat(),
        lng: vertex.lng(),
      };
    });
  }
  return [];
};

/**
 * Gets the polygon's vertices.
 * @return {Object[]} A proxy representing an `Array` of vertex objects with `lat` and `lng` properties.
 */
BeyondMapsPoly.prototype.getVertices = function() {
  return getVertices(this.gmPoly);
};

/**
 * Adds a new vertex to the end of the polygon's list of vertices.
 * @param {Object} vertex An object with `lat` and `lng` properties.
 */
BeyondMapsPoly.prototype.addVertex = function(vertex) {
  this.gmPoly.getPath().push(vertex);
  const path = getVertices(this.gmPoly);

  // If impl is a polyline, calculate and update the distance
  if (path.length > 1 && this.isPolyline) {
    const distance = distanceBetween(...path);
    if (distance > 0) {
      this.totalDistance = distance;
    }
  }

  // Update ghost polygon if it exists
  if (this._ghostPolygon && this._ghostPolygon.getPath().length < 3) {
    this._ghostPolygon.getPath().push(vertex);
  }

  // Update leading line if it exists
  if (this._leadingLine && this._leadingLine.getPath().length < 2) {
    this._leadingLine.getPath().push(vertex);
  }
};

// Helper function for updating the leading line and ghost polygon.
// Updates the last `num` vertices of the shape's path with the passed-in coords, allowing it to follow cursor.
const updateTail = (coords, num) => (poly) => {
  const path = poly.getPath();
  // Done recursively so the pops all happen before the pushes
  const recur = (num) => {
    if (num <= 0) return;
    path.pop(); // remove old tail
    recur(num - 1);
    path.push(coords); // add new tail
  };
  recur(num);
};

/**
 * Sets the editable flag for the polygon.
 * When the flag is `true`, the user can click and drag vertices and add new ones using the Google Maps UI.
 * @param {boolean} newEditable A flag: `true` to make the polygon editable, `false` to make it un-editable.
 */
BeyondMapsPoly.prototype.setEditable = function(newEditable) {
  MapService.setOptions({
    draggableCursor: newEditable ? 'crosshair' : 'grab',
  });
  this.gmPoly.setEditable(newEditable);
};

/**
 * Sets the visible flag for the polygon.
 * When the flag is `true`, the user will be able to see the polygon. It's `true` by default.
 * @param {boolean} newVisible A flag: `true` to make the polygon visible, `false` to make it invisible.
 */
BeyondMapsPoly.prototype.setVisible = function(newVisible) {
  this.gmPoly.setVisible(newVisible);
};

/**
 * Sets whether this polygon has listeners for the leading line & ghost polygon.
 * @param {boolean} newListeners A flag: `true` to register listeners, `false` to remove them.
 * @param {boolean} editing If true, only registers necessary listeners for editing and won't registers listeners for
 * plotting new points
 */
BeyondMapsPoly.prototype.setListeners = function(newListeners, editing = false) {
  if (newListeners) {
  // Register regular listeners for placing new points and closing a polygon
    if (!editing) {
      [clickEventHandler, mousemoveEventHandler, dblclickEventHandler].forEach((handler) =>
        this._listeners.push(this._addListener(MapService.getMap(), handler.eventType, (e) => handler(this, e))));
      // The double-click handler has to be on the polygon because the first click creates a
      // vertex, which would then intercept the dblclick event.
      [clickEventHandler, dblclickEventHandler, closePolyEventHandler].forEach((handler) =>
        this.gmPoly.addListener(handler.eventType, (e) => handler(this, e)));
    } else {
      this.active = false;
    }
    // Add listeners just for editing a completed polygon's vertices and calculating area
    // Re-calculate area whenever a vertex is inserted, moved or deleted
    ['insert_at', 'set_at', 'remove_at'].forEach((eventType) => {
      this.gmPoly.getPath().addListener(eventType, () => {
        if (this.isPolyline) {
          this.totalDistance = distanceBetween(...getVertices(this.gmPoly));
        } else {
          this.area = this.computeArea();
        }
      });
    });

    // Add custom drag event listener for editing a vertex in a polyline
    if (this.isPolyline) {
      // Hides the tooltip when the map is being panned or the cursor leaves the map
      ['mouseout', 'drag'].forEach((listener) => {
        this._listeners.push(this._addListener(MapService.getMap(), listener, () => this.tooltip.style.display = 'none'));
      });

      // Keep tabs on the index being dragged, needs to be at this level of scope to be accessible within
      // the event handler without using an anonymous function
      let vertex;
      // Custom event listener for calculating distance for a polyline being edited by dragging a vertex
      const dragListener = (e) => {
        const path = getVertices(this.gmPoly);
        // convert currently moused screen pixel to lat lng
        const mapPosition = MapService.screenPixelToLatLng(e.offsetX, e.offsetY);
        // replace the vertex being dragged with the new latlng position
        path[vertex] = mapPosition;
        // Calculate distance of modified path using new position and update tooltip
        const distance = distanceBetween(...path);
        this.updateTooltip(distance);
      };
      // Show the current distance when cursoring over a vertex. This also prevents funky calculation behavior
      this.gmPoly.addListener('mouseover', (e) => {
        this.updateTooltip(this.totalDistance);
      });
      // Hide the tooltip when the cursor leaves the vertex to prevent the tooltip from lingering when in edit mode
      this.gmPoly.addListener('mouseout', (e) => {
        this.tooltip.style.display = 'none';
      });
      // On polyline mousedown, set the vertex variable to be used in event handler, hide the leading line,
      // show the tooltip, and register the drag listener
      this.gmPoly.addListener('mousedown', (e) => {
        if (!e.vertex) return;
        vertex = e.vertex;
        this._leadingLine.setVisible(false);
        this.tooltip.style.display = 'inline-block';
        document.addEventListener('mousemove', dragListener);
        // Hide the undo button provided by google maps
        document.querySelectorAll('img[src=\'https://maps.gstatic.com/mapfiles/undo_poly.png\']').forEach((i) => {
          i.parentElement.style.display = 'none';
        });
      });

      // On polyline mouseup, unhide leading line, hide the tooltip, and remove the drag listener
      this.gmPoly.addListener('mouseup', (e) => {
        if (!e.vertex) return;
        // Used to update leading line's position
        if (this.active) {
          mousemoveEventHandler(this, e);
          this._leadingLine.setVisible(true);
        }
        this.tooltip.style.display = 'none';
        document.removeEventListener('mousemove', dragListener);
      });
    }

    this._isListening = true;
  } else {
    this._isListening = false;
    if (this.tooltip) {
      this.tooltip.style.display = 'none';
    }
    removeListeners(this._listeners);
    this.active = false;
  }
  this._helperLines.forEach((poly) => poly.setVisible(newListeners));
};


// Event Listeners
/**
 * @param {BeyondMapsPoly} poly The polygon object
 * @param {MouseEvent} e The click event
 * @return {void}
 */
const clickEventHandler = async (poly, e) => {
  if (e.domEvent.altKey) {
    // Remove vertex if alt key is held
    if ((e.vertex || e?.vertex === 0) && poly.gmPoly.getPath().length > 2) {
      poly.gmPoly.getPath().removeAt(e.vertex);
    }
    return;
  }
  // Falsy value check, so if a vertex other than the first (0) is clicked, don't handle it
  if (e.vertex) return;
  const {
    latLng,
  } = e;
  const path = getVertices(poly.gmPoly);
  if (path.length > 1) {
    const first = path[0];
    // Import is here to prevent errors when this module is loaded before the maps
    // object exists on the Window yet
    const {MapService} = await import('./MapService/index.js');
    const getPixelCoords = (latLng) =>
      Object.values(MapService.latLngToScreenPixel(latLng));
    const firstPxl = getPixelCoords(first);
    const clickPxl = getPixelCoords(latLng);
    const distX = firstPxl[0] - clickPxl[0];
    const distY = firstPxl[1] - clickPxl[1];
    const distance = Math.hypot(distX, distY);
    if (distance <= DBLCLICK_DISTANCE) {
      poly.setListeners(false);
      MapService.setCursor(null);
      // Trigger component watchers
      poly.active = false;
      poly.finished = true;
      if (poly.tooltip) {
        poly.tooltip.remove();
      }
      poly.area = poly.computeArea();
      poly.subscriptions.publish('polygon_created');
    } else {
      poly.addVertex(latLng);
    }
  } else {
    poly.addVertex(latLng);
  }
};

/**
 * @param {BeyondMapsPoly} poly The polygon object
 * @param {google.maps.MapMouseEvent} e The event
 */
const mousemoveEventHandler = (poly, {latLng}) => {
  const updateFn = updateTail(latLng, 1);
  poly._helperLines.forEach((line) => updateFn(line));
  const path = getVertices(poly.gmPoly);
  if (path.length > 0 && poly.isPolyline) {
    poly.updateTooltip(distanceBetween(...path, latLng));
  }
};
const dblclickEventHandler = (poly) => {
  poly.setListeners(false);
  MapService.setCursor(null);
  poly.finished = true;
  poly.subscriptions.publish('polyline_created');
};
/**
 * @param {BeyondMapsPoly} poly The polygon object
 * @param {google.maps.MapMouseEvent} e The event
 */
const closePolyEventHandler = async (poly, {latLng}) => {
  const first = getVertices(poly.gmPoly)[0];
  if (first) {
    // Import is here to prevent errors when this module is loaded before the maps
    // object exists on the Window yet
    const {MapService} = await import('./MapService/index.js');
    const getPixelCoords = (latLng) =>
      Object.values(MapService.latLngToScreenPixel(latLng));
    const firstPxl = getPixelCoords(first);
    const clickPxl = getPixelCoords(latLng);
    const distX = firstPxl[0] - clickPxl[0];
    const distY = firstPxl[1] - clickPxl[1];
    const distance = Math.hypot(distX, distY);
    if (distance <= DBLCLICK_DISTANCE) {
      poly.setListeners(false);
      MapService.setCursor(null);
      poly.finished = true;
    }
  }
};
clickEventHandler.eventType = 'click';
mousemoveEventHandler.eventType = 'mousemove';
dblclickEventHandler.eventType = 'dblclick';
closePolyEventHandler.eventType = 'click';

/**
 * Predicate to test a location for inclusion within the polygon.
 * Delegates to `containsLocation` from the Google Maps geometry API.
 * @param {Object} coords An object with `lat` and `lng` properties.
 * @return {boolean} `true` if the coordinates lie within the polygon, else `false`.
 */
BeyondMapsPoly.prototype.containsLocation = function(coords) {
  return gmAPI.geometry.poly.containsLocation(coords, this.gmPoly);
};

/**
 * Returns the area for a passed in completed path
 * @param {Array} path An array of LatLng objects comprising the path of the poly object
 * @return {number} The computed area of the passed in shape
 */
BeyondMapsPoly.prototype.computeArea = function() {
  const areaInSqFeet = gmAPI.geometry.spherical.computeArea(getVertices(this.gmPoly)) * 10.7639;
  return Math.round(areaInSqFeet);
};

/**
 * Predicate indicating whether this polygon has enough vertices to enclose an area.
 * @return {boolean} `true` if the polygon encloses an area; `false` otherwise.
 */
BeyondMapsPoly.prototype.isEnclosed = function() {
  return this.gmPoly.getPath().length >= 3;
};

/**
 * Updates a Polyline's tooltip with the passed in computed distance
 * @param {string} distance The computed distance of a path
 */
BeyondMapsPoly.prototype.updateTooltip = function(distance) {
  if (distance.length < 1) return;
  this.tooltip.innerHTML = distance;
  this.tooltip.style.display = 'inline-block';
  this.tooltip.style.left = window.event.pageX + 7 + 'px';
  this.tooltip.style.top = window.event.pageY + 7 + 'px';
};

/**
 * Removes the polygon from the map. Must be called when deleting the polygon to stop the overlay from rendering.
 * No further calls should be made to the BeyondMapsPoly after calling this method.
 */
BeyondMapsPoly.prototype.remove = function() {
  this._helperLines.forEach((polyname) => {
    polyname.setMap(null);
    polyname = null;
  });
  this.gmPoly?.setMap(null);
  this.gmPoly = null;
  removeListeners(this._listeners);
  if (this._stopWatch) {
    this._stopWatch();
  }
  this.active = false;
  if (this.tooltip) {
    this.tooltip.remove();
  }
};

const removeListeners = (listenersArr) => {
  listenersArr.forEach((listener) => listener.remove());
  listenersArr.length = 0;
};

export {
  BeyondMapsPoly,
  getVertices,
};
